/*
  Rêve & Silk — tell Brevo who a new subscriber is, in the same browser.

  WHY. Brevo's "Cart abandonment" flow starts from "Cart updated" events,
  and those only reach a contact when Brevo knows which contact this
  browser is (the sib_cuid cookie). A visitor who signs up on one of our
  forms stays anonymous to Brevo until we identify them here.

  NOT A TRACKER. The Brevo Shopify app embed ("brevo-pushowl", enabled in
  config/settings_data.json) already loads Brevo's tracker (sa.js) and its
  own SDK (pushowl-shopify-main.js) on every page. This file adds no
  third-party script. It only calls what the app already put on window.

  CALL PATH — exactly one per identify, first that is ready wins:
    1. window.pushowl (the app's public API), via pushowl.push(fn) so the
       call waits for the app's config. pushowl.identify runs the app's
       own ChannelIdentityManager: it checks the brevo_email flag and ma
       key, stores the email in localStorage pushowl_email (the app reuses
       it as the email on its later tracker events), and then makes the
       same Brevo.identify call the app makes for its own forms. It also
       respects the app's double-opt-in setting if Sam ever turns it on.
    2. window.Brevo, once sa.js has replaced the queue array with the
       tracker object: Brevo.push(fn) then Brevo.identify({ identifiers }).
    3. window.sendinblue.identify (legacy tracker name).
  The app loads its main script about 1.5s after the page, so nothing may
  be ready yet: retry every 500ms for about 10s, then give up silently.

  This file passes no contact attributes of its own (properties: {}).
  Path 1 is not attribute-free, though: pushowl.identify always adds
  subscribe_url (the current page URL, window.location.href) as an
  attribute. Paths 2 and 3 send the email only. The form tags already
  record which surface the visitor signed up on.

  FULL-PAGE POSTS. Every {% form %} on this store is a native POST
  (hCaptcha blocks AJAX — docs/platform-quirks.md), so the page unloads
  right after submit and an XHR from sa.js could be cancelled. So:
    - on submit of a sign-up form that carries consent, store
      { email, source, ts } in sessionStorage (key below);
    - on the next page load, read the outcome Liquid rendered inside the
      form: <template data-rs-signup-result="ok|error">. ok → identify,
      then clear. error → clear without identifying. Neither (for example
      a captcha challenge page) → keep waiting. Entries older than 30
      minutes are dropped.
  The "ok" marker reads form.posted_successfully?, which Shopify sets for
  EVERY customer form on the page (customer_posted is page-scoped). That
  is fine here: the stored entry, written only by our own consenting
  submit, says which email it was.

  WHICH FORMS. Only a form that carries data-rs-brevo-source opts in.
  Consent check at submit, on the values the form will actually post:
    - a contact[Marketing consent] field present → last value must be Yes;
    - a contact[accepts_marketing] field present → last value must be true;
    - neither → the form is a pure newsletter sign-up (the footer).
  A consent-bearing form submitted WITHOUT consent clears any stored
  entry, so an earlier ticked submit of the same visit cannot be
  identified on the next "ok" load.
  Owners of the attribute and the marker (three forms): sections/footer.liquid
  (two forms) and sections/rs-intl-interest.liquid (source "intl",
  2026-10-17). The two teaser forms were removed with Pre-launch mode
  (2026-10-30, Digital #89). See docs/cross-file-registry.md.

  The email never goes into a URL. Never throws; never blocks a submit.
*/
(function () {
  'use strict';

  if (window.rsBrevoIdentify) return;

  var STORE_KEY = 'rs_brevo_identify';
  var MAX_AGE_MS = 30 * 60 * 1000;
  var RETRY_MS = 500;
  var RETRY_LIMIT = 20;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function normalise(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  function readEntry() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.email !== 'string' || typeof entry.ts !== 'number') return null;
      return entry;
    } catch (e) {
      return null;
    }
  }

  function writeEntry(entry) {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(entry)); } catch (e) { /* non-fatal */ }
  }

  function clearEntry() {
    try { window.sessionStorage.removeItem(STORE_KEY); } catch (e) { /* non-fatal */ }
  }

  /* One call path. Returns true when a path took the call. */
  function dispatch(email) {
    try {
      var po = window.pushowl;
      if (po && !Array.isArray(po) && typeof po.identify === 'function') {
        if (typeof po.push === 'function') {
          po.push(function () {
            try { po.identify({ email: email, properties: {} }); } catch (e) { /* non-fatal */ }
          });
        } else {
          po.identify({ email: email, properties: {} });
        }
        return true;
      }

      var brevo = window.Brevo;
      if (brevo && !Array.isArray(brevo) && typeof brevo.identify === 'function' &&
          typeof brevo.push === 'function') {
        brevo.push(function () {
          try { window.Brevo.identify({ identifiers: { email_id: email } }); } catch (e) { /* non-fatal */ }
        });
        return true;
      }

      var sib = window.sendinblue;
      if (sib && typeof sib.identify === 'function') {
        sib.identify(email);
        return true;
      }
    } catch (e) { /* fall through to retry */ }
    return false;
  }

  /* identify(email, source[, done]). source is for the caller's record
     only; it is not sent to Brevo. done(sent) runs once: true after a
     path took the call, false after the retries ran out. */
  function identify(email, source, done) {
    var finish = typeof done === 'function' ? done : function () {};
    var address = normalise(email);
    if (!EMAIL_RE.test(address)) { finish(false); return; }
    var tries = 0;
    (function attempt() {
      if (dispatch(address)) { finish(true); return; }
      tries += 1;
      if (tries >= RETRY_LIMIT) { finish(false); return; }
      window.setTimeout(attempt, RETRY_MS);
    })();
  }

  function lastValue(form, name) {
    var value = null;
    var fields = form.elements;
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (f.name !== name || f.disabled) continue;
      if ((f.type === 'checkbox' || f.type === 'radio') && !f.checked) continue;
      value = f.value;
    }
    return value;
  }

  function hasField(form, name) {
    var fields = form.elements;
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].name === name) return true;
    }
    return false;
  }

  function consentGiven(form) {
    if (hasField(form, 'contact[Marketing consent]') &&
        String(lastValue(form, 'contact[Marketing consent]')).toLowerCase() !== 'yes') return false;
    if (hasField(form, 'contact[accepts_marketing]') &&
        String(lastValue(form, 'contact[accepts_marketing]')).toLowerCase() !== 'true') return false;
    return true;
  }

  /* Capture phase on document: runs before any handler on the form, so a
     handler that stops propagation cannot hide the submit from us. Reads
     only; never cancels the submit. */
  function onSubmit(event) {
    try {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      var source = form.getAttribute('data-rs-brevo-source');
      if (!source) return;
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      /* No consent on THIS submit: drop any entry an earlier consenting
         submit left behind (tick, go back, untick, submit again), so the
         next "ok" load never identifies someone who did not consent. */
      if (!consentGiven(form)) { clearEntry(); return; }
      var email = normalise(lastValue(form, 'contact[email]'));
      if (!EMAIL_RE.test(email)) return;
      writeEntry({ email: email, source: source, ts: Date.now() });
    } catch (e) { /* never block the form */ }
  }

  function resolvePending() {
    var entry = readEntry();
    if (!entry) { clearEntry(); return; }
    var age = Date.now() - entry.ts;
    if (age > MAX_AGE_MS || age < -60000) { clearEntry(); return; }

    if (!entry.ok) {
      if (document.querySelector('template[data-rs-signup-result="ok"]')) {
        /* Mark confirmed first, so a page left before the app is ready
           still identifies on the next page load. */
        entry.ok = true;
        writeEntry(entry);
      } else if (document.querySelector('template[data-rs-signup-result="error"]')) {
        clearEntry();
        return;
      } else {
        return;
      }
    }

    identify(entry.email, entry.source, function () { clearEntry(); });
  }

  window.rsBrevoIdentify = { identify: identify };

  try {
    document.addEventListener('submit', onSubmit, true);
    resolvePending();
  } catch (e) { /* non-fatal */ }
})();
